// TextBotSyncModal.jsx — Sync-Bots web UI (PR-S3)
// Same-tenant TextBot copy-request flow, backed by /api/textbot-sync.
//   Request tab  — ask a teammate (by email) to copy their bots.
//   Incoming tab — requests where I'm the target: pick which of MY bots to
//                  share (default = all), then Accept / Decline.
//   Sent tab     — my outgoing requests with status + Cancel while pending.
// Distinct from the voice-bot share flow (SyncAccount.jsx).

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Inbox, Send, Check, X, Loader2, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet } from '../lib/api.js';
import {
  createSyncRequest,
  listSyncRequests,
  acceptSyncRequest,
  declineSyncRequest,
  cancelSyncRequest,
} from '../api/textBotSync.js';

const TABS = [
  { key: 'request',  label: 'Request',  icon: RefreshCw },
  { key: 'incoming', label: 'Incoming', icon: Inbox },
  { key: 'sent',     label: 'Sent',     icon: Send },
];

const STATUS_STYLE = {
  pending:   'text-yellow-400',
  accepted:  'text-civix-cyan',
  declined:  'text-white/40',
  cancelled: 'text-white/40',
};

export default function TextBotSyncModal({ onClose }) {
  const [tab, setTab] = useState('request');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="panel w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-civix-purple/20 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-civix-purple" />
            </div>
            <h2 className="text-lg font-semibold text-white">Sync Bots</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-1 mb-5 bg-civix-panelLight rounded-lg p-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm rounded-md py-1.5 transition-colors ${
                tab === key ? 'bg-civix-purple/20 text-civix-purple' : 'text-white/50 hover:text-white/80'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {tab === 'request'  && <RequestTab />}
        {tab === 'incoming' && <IncomingTab />}
        {tab === 'sent'     && <SentTab />}
      </div>
    </div>
  );
}

// ─── Request tab ────────────────────────────────────────────────────────────
function RequestTab() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');

  const create = useMutation({
    mutationFn: () => createSyncRequest({ targetEmail: email.trim(), botIds: [] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['textbot-sync', 'sent'] });
      toast.success('Sync request sent');
      setEmail('');
    },
    onError: (err) => toast.error(err.message || 'Failed to send request'),
  });

  function submit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    create.mutate();
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-white/50">
        Ask a teammate to copy their text bots into your account. They approve and choose which bots to share.
      </p>
      <div>
        <label className="block text-xs text-white/50 mb-1">Teammate email *</label>
        <div className="relative">
          <Mail className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="email"
            className="input w-full pl-9"
            placeholder="teammate@yourteam.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </div>
      </div>
      <button type="submit" disabled={create.isPending || !email.trim()} className="btn-primary w-full">
        {create.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Send Request'}
      </button>
    </form>
  );
}

// ─── Incoming tab ───────────────────────────────────────────────────────────
function IncomingTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['textbot-sync', 'incoming'],
    queryFn:  () => listSyncRequests('incoming'),
  });
  const requests = data?.data || [];

  function refresh() {
    qc.invalidateQueries({ queryKey: ['textbot-sync', 'incoming'] });
    qc.invalidateQueries({ queryKey: ['textbot-sync', 'pending-count'] });
  }

  if (isLoading) return <Spinner />;
  const pending = requests.filter((r) => r.status === 'pending');
  if (!pending.length) return <Empty label="No incoming sync requests." />;

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {pending.map((r) => (
        <IncomingCard key={r._id} request={r} onDone={refresh} />
      ))}
    </div>
  );
}

function IncomingCard({ request, onDone }) {
  const [selected, setSelected] = useState(null); // null = all my bots

  const { data } = useQuery({
    queryKey: ['text-bots'],
    queryFn:  () => apiGet('/text-bots'),
  });
  const myBots = (data?.bots || []).filter((b) => b._isOwner !== false);

  const accept = useMutation({
    mutationFn: () => acceptSyncRequest(request._id, selected || []),
    onSuccess: (res) => {
      toast.success(`Copied ${res?.data?.count ?? 0} bot(s) to ${request.requesterEmail}`);
      onDone();
    },
    onError: (err) => toast.error(err.message || 'Failed to accept'),
  });

  const decline = useMutation({
    mutationFn: () => declineSyncRequest(request._id),
    onSuccess: () => { toast.success('Request declined'); onDone(); },
    onError: (err) => toast.error(err.message || 'Failed to decline'),
  });

  function toggle(id) {
    const base = selected ?? myBots.map((b) => b._id);
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    setSelected(next);
  }

  const isChosen = (id) => (selected ? selected.includes(id) : true);
  const busy = accept.isPending || decline.isPending;

  return (
    <div className="bg-civix-panelLight rounded-lg p-4">
      <div className="text-sm text-white mb-1">
        <span className="font-medium">{request.requesterEmail}</span>
        <span className="text-white/50"> wants to copy your bots</span>
      </div>
      <div className="text-xs text-white/40 mb-3">Choose which bots to share (all selected by default).</div>

      <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
        {myBots.length === 0 && <div className="text-xs text-white/40">You have no bots to share.</div>}
        {myBots.map((b) => (
          <label key={b._id} className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
            <input
              type="checkbox"
              checked={isChosen(b._id)}
              onChange={() => toggle(b._id)}
              className="accent-civix-purple"
            />
            {b.name}
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => accept.mutate()}
          disabled={busy || myBots.length === 0}
          className="btn-primary flex-1 flex items-center justify-center gap-1.5"
        >
          {accept.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Accept</>}
        </button>
        <button
          onClick={() => decline.mutate()}
          disabled={busy}
          className="btn-ghost flex-1 flex items-center justify-center gap-1.5"
        >
          {decline.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4" /> Decline</>}
        </button>
      </div>
    </div>
  );
}

// ─── Sent tab ───────────────────────────────────────────────────────────────
function SentTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['textbot-sync', 'sent'],
    queryFn:  () => listSyncRequests('outgoing'),
  });
  const requests = data?.data || [];

  const cancel = useMutation({
    mutationFn: (id) => cancelSyncRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['textbot-sync', 'sent'] });
      toast.success('Request cancelled');
    },
    onError: (err) => toast.error(err.message || 'Failed to cancel'),
  });

  if (isLoading) return <Spinner />;
  if (!requests.length) return <Empty label="You haven't sent any sync requests." />;

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {requests.map((r) => (
        <div key={r._id} className="bg-civix-panelLight rounded-lg p-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-white truncate">{r.targetEmail}</div>
            <div className={`text-xs capitalize ${STATUS_STYLE[r.status] || 'text-white/40'}`}>{r.status}</div>
          </div>
          {r.status === 'pending' && (
            <button
              onClick={() => cancel.mutate(r._id)}
              disabled={cancel.isPending}
              className="btn-ghost text-xs px-3 py-1.5 shrink-0"
            >
              Cancel
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center h-24">
      <Loader2 className="w-5 h-5 text-civix-purple animate-spin" />
    </div>
  );
}

function Empty({ label }) {
  return <div className="text-center text-sm text-white/40 py-8">{label}</div>;
}
